import { useState } from "react";
import { CalendarX, EnvelopeSimple, Warning } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { CandidateEmailModal } from "components/candidates/email";
import type { CandidateInfo } from "components/candidates/types";

export type PlannedVisitsAction = "unpublish" | "delete-timeslot";

interface PlannedVisitsWarningModalProps {
  isOpen: boolean;
  /** Kandidaten met een bezoek dat nog moet plaatsvinden */
  visits: CandidateInfo[];
  /** Bepaalt de tekst: unit offline halen of één tijdslot verwijderen */
  action: PlannedVisitsAction;
  /** Blokkeert de bevestigingsknop terwijl de actie loopt */
  isProcessing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const ACTION_COPY: Record<PlannedVisitsAction, { title: string; description: string; confirmLabel: string }> = {
  unpublish: {
    title: "Er staan nog bezoeken gepland",
    description:
      "De unit offline halen verwijdert de afsprakenkalender, maar annuleert de reeds geboekte bezoeken niet. Deze kandidaten krijgen géén automatische verwittiging.",
    confirmLabel: "Toch offline halen",
  },
  "delete-timeslot": {
    title: "Er staat een bezoek gepland in dit tijdslot",
    description:
      "Het tijdslot verwijderen haalt enkel de beschikbaarheid weg. De afspraak met deze kandidaten blijft bestaan en er vertrekt géén annulatiemail.",
    confirmLabel: "Toch verwijderen",
  },
};

const formatVisitMoment = (visitDate: Date | string | null | undefined): string => {
  if (!visitDate) return "Datum onbekend";
  const date = visitDate instanceof Date ? visitDate : new Date(visitDate);
  if (isNaN(date.getTime())) return "Datum onbekend";
  return date.toLocaleString("nl-BE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const candidateName = (candidate: CandidateInfo): string => {
  const name = `${candidate.personalDetails?.firstName || ""} ${candidate.personalDetails?.lastName || ""}`.trim();
  return name || "Naam onbekend";
};

/**
 * Waarschuwing vóór het weghalen van beschikbaarheid (unit offline halen of een
 * tijdslot verwijderen) wanneer er nog bezoeken geboekt staan.
 *
 * Geboekte bezoeken leven op `AppointmentDetails` en hebben geen relatie met de
 * tijdsloten — ze overleven het verwijderen van de kalender zonder dat de kandidaat
 * iets hoort. Via deze modal kan de gebruiker die kandidaten meteen mailen
 * (hergebruikt `CandidateEmailModal`) of bewust doorgaan.
 */
export const PlannedVisitsWarningModal = ({
  isOpen,
  visits,
  action,
  isProcessing = false,
  onCancel,
  onConfirm,
}: PlannedVisitsWarningModalProps) => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const copy = ACTION_COPY[action];
  const hasEmailableCandidates = visits.some((v) => !!v.personalDetails?.email);
  const buildingAddress = (visits[0] as any)?.unit?.building?.googlePlaceLongAddress ?? null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brick-primary/10">
                <Warning size={24} weight="fill" className="text-brick-primary" />
              </div>
              <DialogTitle className="text-neutral-primary">
                {copy.title} ({visits.length})
              </DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-neutral-secondary-mid">
              {copy.description}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-64 space-y-2 overflow-y-auto py-1">
            {visits.map((visit) => (
              <div
                key={`${visit.id}-${visit.unitId}`}
                className="flex items-start justify-between gap-3 rounded-lg border border-neutral-primary/15 bg-white p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-neutral-primary">
                    {candidateName(visit)}
                  </p>
                  <p className="truncate text-xs text-neutral-secondary-mid">
                    {visit.personalDetails?.email || "Geen e-mailadres"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-neutral-primary">
                  <CalendarX size={14} weight="duotone" className="text-brick-primary" />
                  {formatVisitMoment(visit.visitDate)}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
              Annuleren
            </Button>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsEmailModalOpen(true)}
                disabled={isProcessing || !hasEmailableCandidates}
                title={
                  hasEmailableCandidates
                    ? undefined
                    : "Geen van deze kandidaten heeft een e-mailadres"
                }
              >
                <EnvelopeSimple size={16} weight="bold" />
                Kandidaten mailen
              </Button>
              <Button variant="destructive" onClick={onConfirm} disabled={isProcessing}>
                {copy.confirmLabel}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CandidateEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSuccess={() => setIsEmailModalOpen(false)}
        candidates={visits.filter((v) => !!v.personalDetails?.email)}
        buildingAddress={buildingAddress}
      />
    </>
  );
};

export default PlannedVisitsWarningModal;
